import { useCallback, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import moment from 'moment-timezone';
import { type ChargepointUptimeDay } from 'types/swagger';

interface HeartBeatsChartProps {
  heartbeats: ChargepointUptimeDay[];
  timezone: string;
}

type GroupedData = Pick<ChargepointUptimeDay, 'online'> & {
  startTime: string;
  endTime: string;
};

const HeartBeatDayChart: React.FC<HeartBeatsChartProps> = ({ heartbeats, timezone }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const groupData = useCallback(
    (data: ChargepointUptimeDay[]): GroupedData[] => {
      return data.reduce<GroupedData[]>((acc, entry, index) => {
        if (index === 0 || acc[acc.length - 1].online !== entry.online) {
          acc.push({
            startTime: moment.utc(entry.current_time).tz(timezone).format(),
            endTime: moment.utc(entry.current_time).tz(timezone).format(),
            online: entry.online,
          });
        } else {
          acc[acc.length - 1].endTime = moment.utc(entry.current_time).tz(timezone).format();
        }
        return acc;
      }, []);
    },
    [timezone]
  );

  const drawChart = useCallback(
    (ref: d3.Selection<SVGSVGElement, unknown, null, undefined>, groupedData: GroupedData[]) => {
      const svg = ref;
      const margin = { top: 0, right: 0, bottom: 20, left: 0 };
      const width = +svg.attr('width') - margin.left - margin.right;
      const height = +svg.attr('height') - margin.top - margin.bottom;

      svg.selectAll('*').remove();

      const g = svg
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`)
        .attr('overflow', 'visible');

      const timeExtent = d3.extent(groupedData.map((d) => moment.utc(d.startTime).toDate()));
      if (timeExtent.every((d) => d === undefined)) return;
      const firstTime = timeExtent[0];
      const lastTime = moment.utc(groupedData.at(-1)?.endTime).toDate();

      const x = d3.scaleTime().range([0, width]).domain([firstTime, lastTime]);

      const tickValues = [...d3.timeHour.range(firstTime, lastTime, 1)]
        .map((d) => moment.utc(d).tz(timezone).toDate())
        .filter((d) => d.getMinutes() === 0);

      groupedData?.forEach((group) => {
        const groupStart = moment.utc(group?.startTime).tz(timezone);
        const groupEnd = moment.utc(group?.endTime).tz(timezone);

        const rectWidth = Math.max(1, x(groupEnd.toDate()) - x(groupStart.toDate()) + 1);

        g.append('rect')
          .attr('x', x(groupStart.toDate()))
          .attr('y', -7)
          .attr('width', rectWidth)
          .attr('height', height)
          .attr('fill', group?.online ? '#00FF7F' : '#E6492D');
      });

      g.append('line')
        .attr('x1', 0)
        .attr('y1', height)
        .attr('x2', width)
        .attr('y2', height)
        .attr('margin-top', '10px')
        .attr('stroke', 'rgba(255, 255, 255, 0.6)')
        .attr('stroke-width', '1px');

      const xAxis = d3
        .axisBottom(x)
        .tickValues(tickValues)
        .tickFormat((date) => moment.utc(date.valueOf()).tz(timezone).format('HH:mm'))
        .tickSize(-height)
        .tickPadding(10);

      const xAxisGroup = g.append('g').attr('transform', `translate(0,${height})`).call(xAxis);

      xAxisGroup.select('.domain').remove();
      xAxisGroup.selectAll('line').attr('stroke', 'none');
      xAxisGroup.selectAll('text').attr('fill', 'rgba(255, 255, 255, 1)').attr('font-size', '11px');
    },
    [timezone]
  );

  useEffect(() => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      const groupedData = groupData(heartbeats);
      drawChart(svg, groupedData);
    }
  }, [drawChart, groupData, heartbeats]);

  return <svg ref={svgRef} width="964" height="40" style={{ overflow: 'visible' }} />;
};

export default HeartBeatDayChart;
